import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { ChartBar, Flag } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

function getFormTitle(editing, isMilestone) {
  if (editing) return isMilestone ? 'Modifiér le jalon' : 'Modifiér la tâche';
  return isMilestone ? 'Nouveau jalon' : 'Nouvelle tâche';
}

function getFormDesc(editing, isMilestone) {
  if (isMilestone) return 'Un jalon marque une etape cle du chantier (reception, livraison, contrôle...)';
  if (editing) return 'Modifiéz les informations de la tâche';
  return 'Planifiez une nouvelle tâche pour le chantier';
}

function getSubmitLabel(editing, isMilestone) {
  if (editing) return 'Enregistrér';
  return isMilestone ? 'Créer le jalon' : 'Créer la tâche';
}

export function TaskFormSheet({
  open, onOpenChange, chantiers, entreprises,
  editingTask, formData, setFormData, submitting,
  onSubmit, onFetchEntreprises,
}) {
  const isMilestone = formData.task_type === 'milestone';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="task-form-desc">
        <SheetHeader>
          <SheetTitle>{getFormTitle(editingTask, isMilestone)}</SheetTitle>
          <p id="task-form-desc" className="text-sm text-[#4B5563]">
            {getFormDesc(editingTask, isMilestone)}
          </p>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-5">
          {/* Type toggle */}
          <div className="flex gap-2">
            <Button type="button" variant={formData.task_type === 'task' ? 'default' : 'outline'}
              onClick={() => setFormData({ ...formData, task_type: 'task' })}
              className={`flex-1 h-10 ${formData.task_type === 'task' ? 'bg-[#111827]' : ''}`}
              data-testid="type-task-btn">
              <ChartBar size={16} className="mr-1.5" /> Tâche
            </Button>
            <Button type="button" variant={formData.task_type === 'milestone' ? 'default' : 'outline'}
              onClick={() => setFormData({ ...formData, task_type: 'milestone', progress: 0 })}
              className={`flex-1 h-10 ${formData.task_type === 'milestone' ? 'bg-[#FF6B00] hover:bg-orange-700' : 'border-[#FF6B00] text-[#FF6B00]'}`}
              data-testid="type-milestone-btn">
              <Flag size={16} className="mr-1.5" weight="fill" /> Jalon
            </Button>
          </div>

          <div>
            <Label>Sélectionner un chantier *</Label>
            <Select value={formData.chantier_id}
              onValueChange={(value) => { setFormData({ ...formData, chantier_id: value, entreprise_id: '' }); onFetchEntreprises(value); }}
              disabled={!!editingTask}>
              <SelectTrigger className="mt-1 h-12" data-testid="task-chantier-select">
                <SelectValue placeholder="Sélectionner un chantier" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                {(chantiers || []).map(c => c && (
                  <SelectItem key={c.id} value={c.id} className="py-3">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{isMilestone ? 'Intitule du jalon *' : 'Intitule de la tâche *'}</Label>
            <Input value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={isMilestone ? 'Ex: Reception gros oeuvre' : 'Ex: Coulage dalle RDC'}
              required data-testid="task-title-input" className="mt-1 h-12" />
          </div>

          <div className={`grid gap-3 ${isMilestone ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div>
              <Label>{isMilestone ? 'Date du jalon *' : 'Date de début *'}</Label>
              <Input type="date" value={formData.start_date}
                onChange={(e) => {
                  const newData = { ...formData, start_date: e.target.value };
                  if (isMilestone) newData.end_date = e.target.value;
                  setFormData(newData);
                }}
                required data-testid="task-start-date" className="mt-1 h-12" />
            </div>
            {formData.task_type !== 'milestone' && (
              <div>
                <Label>Date de fin *</Label>
                <Input type="date" value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required data-testid="task-end-date" className="mt-1 h-12" />
              </div>
            )}
          </div>

          {formData.task_type !== 'milestone' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Statut d'avancement</Label>
                <Select value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="mt-1 h-12" data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[100]">
                    <SelectItem value="pending" className="py-3">A realiser</SelectItem>
                    <SelectItem value="in_progress" className="py-3">En cours d'execution</SelectItem>
                    <SelectItem value="completed" className="py-3">Terminée</SelectItem>
                    <SelectItem value="delayed" className="py-3">En retard critique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorite</Label>
                <Select value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger className="mt-1 h-12" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[100]">
                    <SelectItem value="high" className="py-3">Haute</SelectItem>
                    <SelectItem value="medium" className="py-3">Moyenne</SelectItem>
                    <SelectItem value="low" className="py-3">Basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label>Lot / Corps d'etat</Label>
            <Input value={formData.lot}
              onChange={(e) => setFormData({ ...formData, lot: e.target.value })}
              placeholder="Ex: Gros oeuvre"
              data-testid="task-lot-input" className="mt-1 h-12" />
          </div>

          <div>
            <Label>Entreprise</Label>
            {entreprises.length > 0 ? (
              <Select value={formData.entreprise_id || '_manual'}
                onValueChange={(value) => {
                  if (value === '_manual') {
                    setFormData({ ...formData, entreprise_id: '', responsible: '' });
                  } else {
                    const ent = entreprises.find(e => e.id === value);
                    const corps = ent?.corps_etat || '';
                    const sous = (ent?.sous_corps_etat || '').trim();
                    const lotAuto = sous ? `${corps} — ${sous}` : corps;
                    setFormData({
                      ...formData,
                      entreprise_id: value,
                      responsible: ent?.representant || formData.responsible,
                      lot: lotAuto || formData.lot
                    });
                  }
                }}>
                <SelectTrigger className="mt-1 h-12" data-testid="task-entreprise-select">
                  <SelectValue placeholder="Sélectionner une entreprise" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  <SelectItem value="_manual" className="py-3 text-[#6B7280]">Saisie manuelle</SelectItem>
                  {entreprises.map(e => {
                    const sous = (e.sous_corps_etat || '').trim();
                    const suffix = e.corps_etat
                      ? ` (${e.corps_etat}${sous ? ` — ${sous}` : ''})`
                      : '';
                    return (
                      <SelectItem key={e.id} value={e.id} className="py-3">
                        {e.nom}{suffix}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Input value={formData.responsible}
                onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                placeholder="Ex: SARL Construction Plus"
                data-testid="task-responsible-input" className="mt-1 h-12" />
            )}
          </div>

          <div>
            <Label>Responsable / Representant</Label>
            <Input value={formData.responsible}
              onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
              placeholder="Ex: M. Dupont"
              data-testid="task-representant-input" className="mt-1 h-12" />
          </div>

          {formData.task_type !== 'milestone' && (
            <div>
              <div className="flex items-center justify-between">
                <Label>Avancement</Label>
                <span className="text-sm font-bold text-[#FF6B00]">{formData.progress}%</span>
              </div>
              <input type="range" min="0" max="100" step="5"
                value={formData.progress}
                onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
                className="w-full mt-2 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF6B00]"
                data-testid="task-progress-slider" />
              <div className="flex justify-between text-[10px] text-[#9CA3AF] mt-1">
                <span>0%</span><span>50%</span><span>100%</span>
              </div>
            </div>
          )}

          <div>
            <Label>Description</Label>
            <Textarea value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={isMilestone ? 'Details du jalon...' : 'Details de la tâche...'}
              rows={3} data-testid="task-description-input" className="mt-1" />
          </div>

          <div className="flex gap-3 pt-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-12">
              Annuler
            </Button>
            <Button type="submit" disabled={submitting || !formData.chantier_id || !formData.title}
              className="flex-1 h-12 bg-[#FF6B00] hover:bg-orange-700"
              data-testid="task-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : getSubmitLabel(editingTask, isMilestone)}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
